import React, { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Loader2 } from 'lucide-react'
import Modal from './Modal'
import { api } from '../lib/api'

interface EditCollectionModalProps {
  isOpen: boolean
  onClose: () => void
  collection: any
}

interface CollectionForm {
  title: string
  description: string
  can_read: boolean
  can_write: boolean
  media_types: string[]
}

export default function EditCollectionModal({ isOpen, onClose, collection }: EditCollectionModalProps) {
  const [form, setForm] = useState<CollectionForm>({
    title: '',
    description: '',
    can_read: true,
    can_write: true,
    media_types: ['application/stix+json;version=2.1']
  })
  const [error, setError] = useState('')

  const queryClient = useQueryClient()

  // Populate form when collection prop changes
  useEffect(() => {
    if (collection) {
      setForm({
        title: collection.title || '',
        description: collection.description || '',
        can_read: collection.can_read ?? true,
        can_write: collection.can_write ?? true,
        media_types: collection.media_types || ['application/stix+json;version=2.1']
      })
    }
  }, [collection])

  const updateCollectionMutation = useMutation({
    mutationFn: async (collectionData: CollectionForm) => {
      return api.updateCollection(collection.id, collectionData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      onClose()
      setError('')
    },
    onError: (error: Error) => {
      setError(error.message)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!form.title.trim()) {
      setError('Collection title is required')
      return
    }

    updateCollectionMutation.mutate(form)
  }

  const handleClose = () => {
    if (!updateCollectionMutation.isPending) {
      setError('')
      onClose()
    }
  }

  if (!collection) return null

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Collection" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Collection Title *
          </label>
          <input
            type="text"
            id="title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g., Corporate Network IOCs"
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Describe what this collection contains..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <input
              id="can_read"
              type="checkbox"
              checked={form.can_read}
              onChange={(e) => setForm({ ...form, can_read: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="can_read" className="ml-2 block text-sm text-gray-900">
              Allow Read Access
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="can_write"
              type="checkbox"
              checked={form.can_write}
              onChange={(e) => setForm({ ...form, can_write: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="can_write" className="ml-2 block text-sm text-gray-900">
              Allow Write Access
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supported Media Types
          </label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="stix-json"
                checked={form.media_types.includes('application/stix+json;version=2.1')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setForm({
                      ...form,
                      media_types: [...form.media_types, 'application/stix+json;version=2.1']
                    })
                  } else {
                    setForm({
                      ...form,
                      media_types: form.media_types.filter(mt => mt !== 'application/stix+json;version=2.1')
                    })
                  }
                }}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="stix-json" className="ml-2 text-sm text-gray-900">
                STIX 2.1 JSON (application/stix+json;version=2.1)
              </label>
            </div>
          </div>
        </div>

        {/* Collection ID (read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Collection ID (Read-only)
          </label>
          <div className="mt-1 p-3 bg-gray-100 rounded-md">
            <code className="text-sm text-gray-600">{collection.id}</code>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={updateCollectionMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateCollectionMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center"
          >
            {updateCollectionMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {updateCollectionMutation.isPending ? 'Updating...' : 'Update Collection'}
          </button>
        </div>
      </form>
    </Modal>
  )
}